import { Box } from '@chakra-ui/react';
import { EditorContent } from '@tiptap/react';
import { isBrowser } from 'constants/browser';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useFlowPort } from 'modules/Editor/components/FlowCore';
import { memo } from 'react';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import { useEditor as useInfographEditor, useResponsiveGroup, useWidget } from 'widgets/sdk';

import { findFirstFontFamily } from '../common/TextBasedWidgets.helpers';
import { findFirstColor, findFirstFontSize, formatLetterSpacing } from './TextWidget.helpers';
import { Effect, TextEditorProps, TextWidgetData } from './TextWidget.types';
import { DEFAULT_EFFECT } from './defaultTextData';
import { getEffect, getOptimizedEffect } from './textEffect';

export const TextEditor = memo(
  ({ isEditable, editor, onBlur, isTypingRef, textWrapperRef, caretColor }: TextEditorProps) => {
    const { effect = DEFAULT_EFFECT, widgetId } = useWidget<TextWidgetData>();
    const { boundingBox } = useInfographEditor();
    const { letterSpacing = 0 } = useWidget<TextWidgetData>();
    const { responsiveGroupResize } = useResponsiveGroup(widgetId);

    const formattedLetterSpacing = formatLetterSpacing(letterSpacing);
    const { updatePortPosition } = useFlowPort();

    const fitBoundingBoxToTextWhileTyping = ({
      height,
      previousHeight,
    }: {
      height: number | undefined;
      previousHeight: number | undefined;
    }) => {
      boundingBox.updateRect();
      // For multiple - empty lines in Safari the bounding box can be drawn incorrectly - this is a workaround
      if (isBrowser.SAFARI) setTimeout(() => boundingBox.updateRect(), 0);

      if (typeof height !== 'number') return;
      if (!editor) return;
      if (!isEditable) return;

      // only when text is resized from typing in the editor - not when size is updated from the toolbar / keyboard shortcuts
      if (!isTypingRef.current) return;

      // For responsively resizing group members (if any) when text is resized by typing in the editor
      responsiveGroupResize({ height, previousHeight });

      // Render the ports again to update their position based on the new widget size
      updatePortPosition(widgetId);
    };

    const stopPropagation = (e: React.MouseEvent) => {
      if (isEditable) e.stopPropagation();
    };

    const onKeydown = () => {
      if (!isTypingRef.current) isTypingRef.current = true;
    };

    useResizeObserver({ ref: textWrapperRef.current, onResize: fitBoundingBoxToTextWhileTyping });

    if (!editor) return null;

    // The font size of the widget is required for the letter spacing em to scale correctly
    const fontSize = findFirstFontSize(editor);
    const fontFamily = findFirstFontFamily(editor);
    const color = findFirstColor(editor);
    const effects = getEffect({ effect: effect as Effect, color, fontSize: parseInt(fontSize) });

    return (
      <Box
        fontSize={fontSize}
        _hover={{ cursor: isEditable ? 'text' : 'default' }}
        h='fit-content'
        w='100%'
        cursor={isEditable ? '' : 'move !important'}
        position='relative'
        onClick={stopPropagation}
        onMouseDown={stopPropagation}
        ref={textWrapperRef}
        onBlur={onBlur}
        style={{ ...effects, letterSpacing: formattedLetterSpacing }}
        onKeyDown={onKeydown}
        // The input cursor color set to the same color as the text (applies for blank lines without text)
        color={caretColor}
        // for line-height consistency when exporting to pdf - the parent div should have the same font family as the editor
        // obs: there are probably edge-cases here with multiple fonts in the same text widget
        fontFamily={fontFamily}
      >
        <EditorContent editor={editor} />
      </Box>
    );
  },
);
TextEditor.displayName = 'TextEditor';

export const ReadOnlyTextEditor = ({ editor }: any) => {
  const { letterSpacing = 0, effect = DEFAULT_EFFECT, renderType } = useWidget<TextWidgetData>();

  const fontSize = findFirstFontSize(editor);
  const fontFamily = findFirstFontFamily(editor);
  const formattedLetterSpacing = formatLetterSpacing(letterSpacing);
  const color = findFirstColor(editor);

  // TODO: remove this when we have a better solution for exporting text effects
  // (or if it's not needed at all for future playwright versions)
  const isExportRender = renderType === ReadOnlyRenderType.ExportStructureTreeList;
  const optimize = renderType === ReadOnlyRenderType.ThumbnailReadOnly;

  const effects = getOptimizedEffect({
    effect: effect as Effect,
    color,
    fontSize: parseInt(fontSize),
    shouldSetUniqueShadowColor: isExportRender,
    optimize,
  });
  const hasEffect = !!effect?.type;

  return (
    <Box
      fontSize={fontSize}
      letterSpacing={formattedLetterSpacing}
      style={effects}
      h='fit-content'
      w='100%'
      position='relative'
      // for line-height consistency when exporting to pdf - the parent div should have the same font family as the editor
      // obs: there are probably edge-cases here with multiple fonts in the same text widget
      fontFamily={fontFamily}
      aria-hidden={!!hasEffect}
    >
      <EditorContent editor={editor} />
    </Box>
  );
};
